import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useMgmtBrand } from "@/lib/useMgmtBrand";

/**
 * Reusable branded forgot-password page for both the Worker and
 * Management-USA-NJ admin portals. Never surfaces Catskills branding
 * and never links back to /login.
 */
export default function BrandedForgotPassword({
  brand, // { name, tagline, apiPrefix, loginPath }
  testidPrefix,
}) {
  useMgmtBrand(`${brand.name} — Forgot password`);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`${brand.apiPrefix}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-black tracking-tighter" data-testid={`${testidPrefix}-title`}>
            {brand.name}
          </h1>
          <p className="text-sm text-slate-400 mt-1">{brand.tagline}</p>
        </div>

        <div className="bg-slate-800/60 border border-slate-700 rounded-md p-6 shadow-xl">
          {sent ? (
            <div className="text-center space-y-3" data-testid={`${testidPrefix}-sent`}>
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-600/20 flex items-center justify-center">
                <Mail className="w-6 h-6 text-emerald-300" />
              </div>
              <h2 className="font-semibold text-lg">Check your email</h2>
              <p className="text-sm text-slate-300">
                If an account exists for <b>{email}</b>, a password-reset link has been sent. It expires in 24 hours.
              </p>
              <Link
                to={brand.loginPath}
                className="inline-block text-sm text-emerald-300 hover:text-emerald-200 underline underline-offset-4 mt-4"
                data-testid={`${testidPrefix}-back-login`}
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid={`${testidPrefix}-form`}>
              <div>
                <h2 className="font-semibold text-lg">Forgot your password?</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Enter your work email and we&apos;ll send you a link to set a new one.
                </p>
              </div>
              <div>
                <Label htmlFor="fpemail" className="text-slate-300">Email</Label>
                <Input
                  id="fpemail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="bg-slate-900 border-slate-700 text-slate-50"
                  data-testid={`${testidPrefix}-email`}
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid={`${testidPrefix}-submit`}
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              <div className="text-center text-sm">
                <Link
                  to={brand.loginPath}
                  className="text-slate-400 hover:text-slate-200"
                  data-testid={`${testidPrefix}-back-login`}
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
